import React, {useCallback, useEffect, useReducer, useState, useRef} from 'react';
import NavDropdown from 'react-bootstrap/NavDropdown';
import Dropdown from 'react-bootstrap/Dropdown';
import Nav from 'react-bootstrap/Nav';
import {FormModal} from 'components/modals';
import {Tooltip} from '@/components/tooltips';
import {Icon} from '@/components/icons/Icon';
import {useStoreActions, useStoreState} from 'easy-peasy';
import ChartSettingsModal from './chart_settings';
import NewStoryboardModal from './storyboard_settings/NewStoryboardModal';
import {chartQueryDefaultProps} from './utils/chartPropTypes';
import StoryboardLink from './storyboard_settings/StoryboardLink';
import StoryboardSettingsModal from './storyboard_settings/StoryboardSettingsModal';
import StoryboardSharingModal from './storyboard_settings/StoryboardSharingModal';
import StorySettingsForm from '@/views/stories/StorySettingsForm';
import useOptimizedConfig from '@/hooks/useOptimizedConfig';
import sleep from '@/utils/sleep';
import i18n from 'i18n';
import {ChartQuery} from '@/entities/chart';
import {hasPermission} from '@/utils/permissions/permissionDebug';
import {Button} from 'components/ui';

interface StoryboardData {
    title: string;
    id: number;
}

interface State {
    newStoryboardModalOpen: boolean;
    storyboardSettingsModalOpen: boolean;
    storyboardSharingModalOpen: boolean;
    selectedChartQuery: ChartQuery;
    selectedStoryboardData: StoryboardData;
}

interface Storyboard {
    id: number;
    title: string;
    writeAccess: boolean;
    storyId: number;
    storySlug?: string;
    charts: any[];
}

interface UserData {
    permissions?: string[];
}

interface StoryboardMenuBarProps {
    onNewChartSave: (chartData: any) => void;
    onNewStoryboardSave: (storyboardData: any) => void;
    onStoryboardModify: (storyboardData: any) => void;
    moveStoryboard: (dragIndex: number, hoverIndex: number) => void;
    onStoryboardMove: () => void;
    deleteStoryboard: (index: number) => void;
    chartSettingsModalOpen: boolean;
    chartSettingsModalToggle: () => void;
    activeStoryboardIndex: number;
    storyboards: Storyboard[];
    storyIsPublic?: boolean;
    isRearrangeMode: boolean;
    hasUnsavedChanges: boolean;
    onToggleRearrangeMode: () => void;
    onSaveLayout: () => void;
    onCancelRearrange: () => void;
}

type Action =
    | {type: 'toggleNewStoryboardModal'}
    | {type: 'toggleStoryboardSettingsModal'}
    | {type: 'toggleStoryboardSharingModal'}
    | {type: 'setSelectedChartQuery'; payload: ChartQuery}
    | {type: 'setSelectedStoryboardData'; payload: StoryboardData};

const reducer = (state: State, action: Action): State => {
    switch (action.type) {
        case 'toggleNewStoryboardModal':
            return {...state, newStoryboardModalOpen: !state.newStoryboardModalOpen};
        case 'toggleStoryboardSettingsModal':
            return {...state, storyboardSettingsModalOpen: !state.storyboardSettingsModalOpen};
        case 'toggleStoryboardSharingModal':
            return {...state, storyboardSharingModalOpen: !state.storyboardSharingModalOpen};
        case 'setSelectedChartQuery':
            return {...state, selectedChartQuery: action.payload};
        case 'setSelectedStoryboardData':
            return {...state, selectedStoryboardData: action.payload};
        default:
            throw new Error('Invalid action type');
    }
};

const StoryboardMenuBar: React.FC<StoryboardMenuBarProps> = React.memo((
    {
        onNewChartSave,
        onNewStoryboardSave,
        onStoryboardModify,
        moveStoryboard,
        onStoryboardMove,
        deleteStoryboard,
        chartSettingsModalOpen,
        chartSettingsModalToggle,
        activeStoryboardIndex,
        storyboards,
        storyIsPublic,
        isRearrangeMode,
        hasUnsavedChanges,
        onToggleRearrangeMode,
        onSaveLayout,
        onCancelRearrange,
    },
) => {
    // ALL HOOKS AT THE TOP - CRITICAL FOR RULES OF HOOKS
    const [chartSettingsModalType, setChartSettingsModalType] = useState<'chart' | 'comment'>('chart');
    const [storySettingsModalOpen, setStorySettingsModalOpen] = useState(false);
    const isMountedRef = useRef(true);

    const {userData, loading: loadingUserData} = useOptimizedConfig();

    const [state, dispatch] = useReducer(reducer, {
        newStoryboardModalOpen: false,
        storyboardSettingsModalOpen: false,
        storyboardSharingModalOpen: false,
        selectedChartQuery: chartQueryDefaultProps,
        selectedStoryboardData: {
            title: '',
            id: -1,
        },
    });

    const setFilterSidebarOpen = useStoreActions((actions: any) => actions.filterSidebarOpen.setFilterSidebarOpen);
    const setFilterSidebarOpenMem = useStoreActions((actions: any) => actions.filterSidebarOpen.setFilterSidebarOpenWithMemory);
    const commitDummyStory = useStoreActions((actions: any) => actions.storyboards.commitDummyStory);
    const duplicateStory = useStoreActions((actions: any) => actions.storyboards.duplicateStory);

    const searchQueryAllFields = useStoreState((state: any) => state.search.searchQueryAllFields);
    const userIsStoryAdmin = useStoreState((state: any) => state.storyboards.userIsStoryAdmin);
    const filterSidebarOpen = useStoreState((state: any) => state.filterSidebarOpen.filterSidebarOpen);

    const onChartSettingsModalClose = useCallback(() => {
        dispatch({type: 'setSelectedChartQuery', payload: chartQueryDefaultProps});
        chartSettingsModalToggle();
    }, [dispatch, chartSettingsModalToggle]);

    const haveGlobalFilters = searchQueryAllFields && Object.keys(searchQueryAllFields).length;

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    useEffect(() => {
        setFilterSidebarOpenMem(filterSidebarOpen || (chartSettingsModalOpen && haveGlobalFilters));
    }, [
        chartSettingsModalOpen,
        haveGlobalFilters,
    ]);

    useEffect(() => {
        if (haveGlobalFilters) {
            setFilterSidebarOpen(!!haveGlobalFilters);
        }
    }, [
        haveGlobalFilters,
    ]);

    useEffect(() => {
        const waitAnimationFinishAndResetModalType = async () => {
            await sleep(100);
            if (isMountedRef.current) {
                setChartSettingsModalType('chart');
            }
        };

        if (!chartSettingsModalOpen) {
            waitAnimationFinishAndResetModalType();
        }
    }, [
        chartSettingsModalOpen,
        setChartSettingsModalType,
    ]);


    const selectStoryboard = (storyboardData: StoryboardData) => {
        dispatch({type: 'setSelectedStoryboardData', payload: storyboardData});
    };

    const openStoryboardSettingsModal = (storyboardData: StoryboardData) => {
        selectStoryboard(storyboardData);
        dispatch({type: 'toggleStoryboardSettingsModal'});
    };

    const openStoryboardSharingModal = (storyboardData: StoryboardData) => {
        selectStoryboard(storyboardData);
        dispatch({type: 'toggleStoryboardSharingModal'});
    };

    const {
        selectedChartQuery,
        selectedStoryboardData,
        newStoryboardModalOpen,
        storyboardSettingsModalOpen,
        storyboardSharingModalOpen,
    } = state;

    // Early return if userData is not loaded yet - AFTER all hooks are defined
    if (loadingUserData || !userData) {
        return null;
    }

    const userCanChange = userIsStoryAdmin || storyboards[activeStoryboardIndex].writeAccess || (storyIsPublic && hasPermission('fs.story.storyboard.public.update', userData?.permissions, true));

    const userCanMakePublicCopy = hasPermission('fs.story.make_public.execute', userData?.permissions, true);
    const isDummyStory = storyboards[0].id < 0;

    const isNumericalOrSelectionStory = storyboards[0].storySlug === 'numerical_descriptor' || storyboards[0].storySlug === 'selection';

    const storyboardLinks = Array.isArray(storyboards) ? storyboards.map((obj, idx) => (
        <StoryboardLink
            key={obj.id}
            id={obj.id}
            writeAccess={obj.writeAccess}
            title={obj.title}
            index={idx}
            activeStoryboardIndex={activeStoryboardIndex}
            openStoryboardSettingsModal={() => openStoryboardSettingsModal({
                title: obj.title,
                id: obj.id,
            })}
            openStoryboardSharingModal={() => openStoryboardSharingModal({
                title: obj.title,
                id: obj.id,
            })}
            moveStoryboardLink={(dragIndex: number, hoverIndex: number) => moveStoryboard(dragIndex, hoverIndex)}
            onStoryboardMove={() => onStoryboardMove()}
            onStoryboardDelete={(index: number) => deleteStoryboard(index)}
            onlyStoryboard={storyboards.length === 1}
        />
    )) : [];

    let newChartButton: React.ReactElement | null = null;
    let newCommentButton: React.ReactElement | null = null;

    if (storyboards[activeStoryboardIndex].writeAccess) {
        const chartButtonElement = (
            <div>
                <NavDropdown.Item
                    eventKey="2"
                    disabled={!storyboards.length}
                    onClick={() => {
                        setChartSettingsModalType('chart');
                        chartSettingsModalToggle();
                    }}
                    active={false}
                    data-cy="new-chart-btn"
                >
                    <Icon name="mdi-plus" /> {i18n.t('jt_new_chart.text')}
                </NavDropdown.Item>
            </div>
        );

        const commentButtonElement = (
            <div>
                <NavDropdown.Item
                    eventKey="3"
                    disabled={!storyboards.length}
                    onClick={() => {
                        setChartSettingsModalType('comment');
                        chartSettingsModalToggle();
                    }}
                    active={false}
                    data-cy="new-comment-btn"
                >
                    <Icon name="mdi-plus" /> {i18n.t('jt_new_comment.text')}
                </NavDropdown.Item>
            </div>
        );

        if (storyboards.length === 0) {
            newChartButton = (
                <Tooltip
                    content={i18n.t('jt_create_storyboard.text')}
                    placement="left"
                >
                    {chartButtonElement}
                </Tooltip>
            );

            newCommentButton = (
                <Tooltip
                    content={i18n.t('jt_create_storyboard.text')}
                    placement="left"
                >
                    {commentButtonElement}
                </Tooltip>
            );
        } else {
            newChartButton = chartButtonElement;
            newCommentButton = commentButtonElement;
        }
    }

    let selectedChartQueryProp: {chartQuery?: ChartQuery} = {};
    if (selectedChartQuery && Object.keys(selectedChartQuery).length !== 0) {
        selectedChartQueryProp = {chartQuery: selectedChartQuery};
    }

    let personalCopyAction: () => void;
    if (isDummyStory) {
        personalCopyAction = () => commitDummyStory({publicCommit: false});
    } else {
        personalCopyAction = () => duplicateStory(storyboards[0].storyId);
    }

    let addBtnClasses = '';
    if (!storyboards.length) {
        addBtnClasses = 'mb-3';
    }

    const rearrangeButton = !isDummyStory ? (
        userCanChange ? (
            isRearrangeMode ? (
                <Tooltip content={i18n.t('jt_exit_rearrange.text')} placement="top">
                    <Button
                        variant="outline-danger"
                        size="sm"
                        icon="mdi-close"
                        className={addBtnClasses}
                        onClick={onToggleRearrangeMode}
                        data-cy="exit-rearrange-btn"
                    />
                </Tooltip>
            ) : (
                <Tooltip content={i18n.t('jt_rearrange_charts.text')} placement="top">
                    <Button
                        variant="outline-info"
                        size="sm"
                        icon="mdi-sort"
                        className={addBtnClasses}
                        onClick={onToggleRearrangeMode}
                        data-cy="rearrange-charts-btn"
                    />
                </Tooltip>
            )
        ) : (
            <Tooltip content={i18n.t('tt_you_dont_have_permission_to_change_the_storyboard.text')} placement="top">
                <span className="d-inline-block">
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        icon="mdi-sort"
                        className={addBtnClasses}
                        data-cy="rearrange-charts-btn"
                        disabled
                        style={{pointerEvents: 'none'}}
                    />
                </span>
            </Tooltip>
        )
    ) : null;

    const saveLayoutButtons = (isRearrangeMode && hasUnsavedChanges && userCanChange && !isDummyStory) ? (
        <div className="d-flex mr-2 align-items-center">
            <Tooltip content={i18n.t('jt_save_layout.text')} placement="top">
                <Button
                    variant="success"
                    className="mb-2 mt-0 d-flex align-items-center"
                    size="sm"
                    onClick={onSaveLayout}
                    data-cy="save-layout-btn"
                    style={{height: '35px'}}
                >
                    <Icon name="mdi-check" size="sm" /> {i18n.t('jt_save_layout.text')}
                </Button>
            </Tooltip>
        </div>
    ) : null;

    const settingDropdowns = (
        <div className="ml-auto d-flex align-items-center flex-wrap mb-2">
            {saveLayoutButtons}
            {
                !isDummyStory ? (
                    <span className="d-inline-flex align-items-center mr-2">
                        {userCanChange ? (
                            <Tooltip
                                content={i18n.t('jt_add.text')}
                                placement="top"
                            >
                                <Dropdown id="nav-dropdown">
                                    <Dropdown.Toggle
                                        as={Button}
                                        variant="outline-success"
                                        size="sm"
                                        icon="mdi-plus"
                                        className={`dropdown-toggle-no-caret ${addBtnClasses}`}
                                        data-cy="new-item-toggle"
                                    />

                                <Dropdown.Menu align="end">
                                    {userIsStoryAdmin ? (
                                        <NavDropdown.Item
                                            eventKey="1"
                                            active={false}
                                            onClick={() => dispatch({type: 'toggleNewStoryboardModal'})}
                                            data-cy="new-storyboard-btn"
                                        >
                                            <Icon name="mdi-plus" /> {i18n.t('jt_new_storyboard.text')}
                                        </NavDropdown.Item>
                                    ) : null}

                                    {newChartButton}
                                    {newCommentButton}
                                </Dropdown.Menu>
                            </Dropdown>
                        </Tooltip>
                        ) : (
                            <Tooltip
                                content={i18n.t('tt_you_dont_have_permission_to_change_the_storyboard.text')}
                                placement="top"
                            >
                                <span className="d-inline-block">
                                    <Button
                                        variant="outline-secondary"
                                        size="sm"
                                        icon="mdi-plus"
                                        className={addBtnClasses}
                                        data-cy="new-item-toggle"
                                        disabled
                                        style={{pointerEvents: 'none'}}
                                    />
                                </span>
                            </Tooltip>
                        )}
                    </span>
                ) : null
            }

            {rearrangeButton ? (
                <span className="d-inline-flex align-items-center mr-2">
                    {rearrangeButton}
                </span>
            ) : null}

            <span className="d-inline-flex align-items-center">
                <Tooltip
                    content={i18n.t('jt_settings.text')}
                    placement="top"
                >
                    <Dropdown id="nav-dropdown-2">
                        <Dropdown.Toggle
                            as={Button}
                            variant="outline-info"
                            size="sm"
                            icon="mdi-settings"
                            className={`dropdown-toggle-no-caret ${addBtnClasses}`}
                            data-cy="storyboard-menu-bar-outlineinfo-btn"
                        />

                        <Dropdown.Menu align="end">
                            {
                                userCanChange && userCanMakePublicCopy && isDummyStory && !isNumericalOrSelectionStory ? (
                                    <NavDropdown.Item
                                        eventKey="1"
                                        active={false}
                                        onClick={() => commitDummyStory({publicCommit: true})}
                                    >
                                        <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_edit_this.text')}
                                    </NavDropdown.Item>
                                ) : null
                            }

                            {
                                userCanChange && (userCanMakePublicCopy && userIsStoryAdmin) && !isDummyStory ? (
                                    <NavDropdown.Item
                                        eventKey="1"
                                        active={false}
                                        onClick={() => setStorySettingsModalOpen(true)}
                                    >
                                        <Icon name="mdi-settings" className="mr-2" /> {i18n.t('jt_story_settings.text')}
                                    </NavDropdown.Item>
                                ) : null
                            }

                            <NavDropdown.Item
                                eventKey="1"
                                active={false}
                                onClick={personalCopyAction}
                                data-cy="storyboard-menu-bar-null-btn">
                                <Icon name="mdi-content-copy" className="mr-2" /> {i18n.t('jt_make_personal.text')}
                            </NavDropdown.Item>
                        </Dropdown.Menu>
                        </Dropdown>
                    </Tooltip>
            </span>
        </div>
    );

    return (
        <Nav className="mb-3 px-3" variant="tabs" data-cy="storyboard-menubar">
            {storyboardLinks}
            {settingDropdowns}

            <NewStoryboardModal
                onSave={(storyboardData: any) => {
                    dispatch({type: 'toggleNewStoryboardModal'});
                    onNewStoryboardSave(storyboardData);
                }}
                storyId={storyboards[0].storyId}
                modalProps={{
                    show: newStoryboardModalOpen,
                    onHide: () => dispatch({type: 'toggleNewStoryboardModal'}),
                }}
            />

            <StoryboardSettingsModal
                storyboardData={selectedStoryboardData}
                onSave={(storyboardData: any) => {
                    dispatch({type: 'toggleStoryboardSettingsModal'});
                    onStoryboardModify(storyboardData);
                }}
                modalProps={{
                    show: storyboardSettingsModalOpen,
                    onHide: () => dispatch({type: 'toggleStoryboardSettingsModal'}),
                }}
            />

            <StoryboardSharingModal
                storyboardData={selectedStoryboardData}
                modalProps={{
                    show: storyboardSharingModalOpen,
                    onHide: () => dispatch({type: 'toggleStoryboardSharingModal'}),
                }}
            />

            <ChartSettingsModal
                key={`null-${chartSettingsModalOpen}`}
                comment={chartSettingsModalType === 'comment'}
                {...selectedChartQueryProp}
                activeStoryboardId={storyboards.length ? storyboards[activeStoryboardIndex].id : null}
                onSave={(chartData: any) => {
                    onNewChartSave(chartData);
                    chartSettingsModalToggle();
                }}
                onModalHide={onChartSettingsModalClose}
                modalProps={{
                    show: chartSettingsModalOpen,
                }}
            />

            <FormModal
                isOpen={storySettingsModalOpen}
                onClose={() => setStorySettingsModalOpen(false)}
                title={i18n.t('jt_story_settings.text')}
            >
                <StorySettingsForm
                    storyId={storyboards[0].storyId}
                    onDone={() => setStorySettingsModalOpen(false)}
                />
            </FormModal>
        </Nav>
    );
});

export default StoryboardMenuBar; 